import { useCallback, useEffect, useRef, useState } from 'react';

export type ColorMode = 'light' | 'dark';
const STORAGE_KEY = 'geisti.colorMode';
const QUERY = '(prefers-color-scheme: dark)';

function readStoredMode(): ColorMode | null {
  if (typeof window === 'undefined') return null;
  const v = window.localStorage.getItem(STORAGE_KEY);
  return v === 'light' || v === 'dark' ? v : null;
}

function readSystemMode(): ColorMode {
  if (typeof window === 'undefined' || !window.matchMedia) return 'light';
  return window.matchMedia(QUERY).matches ? 'dark' : 'light';
}

export default function useColorMode(): [ColorMode, (m: ColorMode) => void] {
  const [stored, setStored] = useState<ColorMode | null>(() => readStoredMode());
  const [systemMode, setSystemMode] = useState<ColorMode>(() => readSystemMode());
  const hasOverrideRef = useRef<boolean>(stored !== null);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mql = window.matchMedia(QUERY);
    const handler = (e: MediaQueryListEvent | { matches: boolean }) => {
      if (hasOverrideRef.current) return;
      setSystemMode(e.matches ? 'dark' : 'light');
    };
    mql.addEventListener('change', handler as (e: MediaQueryListEvent) => void);
    return () => mql.removeEventListener('change', handler as (e: MediaQueryListEvent) => void);
  }, []);

  const setMode = useCallback((m: ColorMode) => {
    hasOverrideRef.current = true;
    setStored(m);
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(STORAGE_KEY, m);
    }
  }, []);

  const mode: ColorMode = stored ?? systemMode;
  return [mode, setMode];
}
